import { useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useJobPolling } from "@/hooks/useJobPolling";
import { useBeforeUnload } from "@/hooks/useBeforeUnload";
import { formatTime } from "@/lib/utils";
import type { LogMessage } from "@/types";

export default function ProcessingPage() {
  const { id: meetingId } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const jobId = params.get("job");
  const navigate = useNavigate();

  const outcome = useJobPolling(jobId);
  const isRunning = outcome?.kind === "running";

  // 處理中時防止離開
  useBeforeUnload(isRunning, "會議紀錄處理中，離開會中斷正在處理的程序");

  // 完成時自動跳到詳情頁
  useEffect(() => {
    if (outcome?.kind === "completed" && meetingId) {
      const t = setTimeout(() => navigate(`/meeting/${meetingId}`), 1200);
      return () => clearTimeout(t);
    }
  }, [outcome, meetingId, navigate]);

  if (!jobId) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12 text-center">
        <p className="text-red-700">缺少 job 參數</p>
      </div>
    );
  }

  const job = outcome && "job" in outcome ? outcome.job : null;
  const progress = job?.progress_percent ?? 0;
  const stepLabel = job?.step_label ?? "啟動中⋯⋯";
  const logs: LogMessage[] = job?.log_messages
    ? (JSON.parse(job.log_messages) as LogMessage[])
    : [];

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <h1 className="font-serif text-3xl font-bold text-brand-dark mb-2">
        {outcome?.kind === "completed"
          ? "完成了"
          : outcome?.kind === "failed"
            ? "處理失敗"
            : "正在整理⋯⋯"}
      </h1>
      <p className="text-sm text-brand-dark/60 mb-10">
        {outcome?.kind === "completed"
          ? "正要帶你去預覽"
          : outcome?.kind === "failed"
            ? "下方有錯誤訊息"
            : "別關掉這個分頁，處理需要 30 秒～數分鐘"}
      </p>

      {/* 進度條 */}
      <div className="mb-3">
        <div className="flex justify-between items-baseline mb-2">
          <span className="text-sm font-medium text-brand-dark">
            {stepLabel}
          </span>
          <span className="text-2xl font-bold num text-brand-gold">
            {progress}%
          </span>
        </div>
        <div className="progress-track">
          <div
            className="progress-bar"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* 失敗訊息 */}
      {outcome?.kind === "failed" && job && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm font-medium text-red-800 mb-1">
            {job.error_code ?? "ERROR"}
          </div>
          <div className="text-sm text-red-700">{job.error_message}</div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => navigate(`/meeting/${meetingId}`)}
              className="text-xs px-3 py-1.5 bg-red-700 text-white rounded-md"
            >
              查看會議
            </button>
            <button
              onClick={() => navigate("/new")}
              className="text-xs px-3 py-1.5 border border-red-200 text-red-800 rounded-md"
            >
              重新開始
            </button>
          </div>
        </div>
      )}

      {outcome?.kind === "error" && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
          連線異常：{outcome.message}（自動重試中⋯⋯）
        </div>
      )}

      {/* 滾動 log */}
      <LogStream logs={logs} />

      {/* 離開警示 */}
      {isRunning && (
        <div className="mt-6 p-3 bg-brand-gold/10 border border-brand-gold/30 rounded-md text-xs text-brand-dark/70 flex items-start gap-2">
          <svg
            className="w-4 h-4 flex-shrink-0 mt-0.5 text-brand-gold"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
            <path d="M12 8v4M12 16h0" strokeWidth="1.5" />
          </svg>
          <div>
            處理中請保持此分頁開啟。若你切換 tab，瀏覽器會發通知提醒你回來。
          </div>
        </div>
      )}
    </div>
  );
}

function LogStream({ logs }: { logs: LogMessage[] }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [logs]);

  return (
    <div className="mt-8">
      <div className="text-xs text-brand-dark/60 mb-2 uppercase tracking-wider">
        處理紀錄
      </div>
      <div
        ref={ref}
        className="bg-brand-dark text-brand-cream rounded-md p-3 h-64 overflow-y-auto font-mono text-xs leading-relaxed"
      >
        {logs.length === 0 ? (
          <div className="text-brand-cream/40 p-2">等待開始⋯⋯</div>
        ) : (
          logs.map((log, i) => (
            <div
              key={i}
              className={
                log.level === "error"
                  ? "text-red-300"
                  : log.level === "warn"
                    ? "text-yellow-300"
                    : log.level === "success"
                      ? "text-brand-green"
                      : "text-brand-cream/80"
              }
            >
              <span className="text-brand-cream/40 num mr-2">
                {formatTime(log.timestamp)}
              </span>
              {log.message}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
